import { useCallback, useEffect, useState } from 'react';
import { getCanvasConfigV3 } from '$/domains/workFlowV3/workFlowV3Service';
import type {
  MediaToolsModelConfig,
  MediaToolsModelToolConfig,
  ModelToolParameterItem,
} from '$/domains/workFlowV3/type';
import { getDefaultParams } from '$/pages/RoboneoTeamFlow/utils/modelConfig';

export type RoleGenModelParameterValue = string | number | boolean;

/** 模型下拉项；价格字段由后端下发，前端仅展示。 */
export interface RoleGenModelOption {
  key: string;
  modelId: string;
  name: string;
  description?: string;
  icon?: string;
  apiName: string;
  nodeModelId: string;
  parameters: Record<string, RoleGenModelParameterValue>;
  parameterConfigs: Record<string, ModelToolParameterItem>;
  previewPrice?: number;
  threeViewPrice?: number;
}

type CharacterModelConfig = {
  character?: MediaToolsModelConfig[];
};

/** 角色模型展示名沿用画布模型菜单优先级，保证中英文配置都可回退。 */
function getCharacterModelLabel(model: MediaToolsModelConfig): string {
  return model.model_name || model.cn_name || model.oversea_name || '角色模型';
}

/** 角色创建目前按 character 模型首个工具提交，参数和计费都绑定同一工具。 */
function getCharacterPrimaryTool(model: MediaToolsModelConfig): MediaToolsModelToolConfig | undefined {
  return model.tool_list?.[0];
}

/** 将 /v3/canvas/config.json 的 character 分组归一化为创建角色底部菜单数据。 */
function getCharacterModelOptions(models: CharacterModelConfig | undefined): RoleGenModelOption[] {
  const characterModels = Array.isArray(models?.character) ? models.character : [];
  return characterModels.map((model, index) => {
    const tool = getCharacterPrimaryTool(model);
    return {
      key: `${model.model_id || 'character'}-${index}`,
      modelId: model.model_id,
      name: getCharacterModelLabel(model),
      description: model.model_desc,
      icon: model.model_iconfont,
      apiName: tool?.api_name ?? '',
      nodeModelId: tool?.node_model_id ?? '',
      parameters: getDefaultParams(tool?.parameters) as Record<string, RoleGenModelParameterValue>,
      parameterConfigs: tool?.parameters ?? {},
    };
  });
}

/**
 * 拉取角色生成可用模型列表，复用画布 /v3/canvas/config.json 的 character 分组。
 *
 * @returns 模型列表 + loading / error / reload
 */
export default function useModelOptions(): {
  options: RoleGenModelOption[];
  loading: boolean;
  error: Error | null;
  reload: () => void;
} {
  const [options, setOptions] = useState<RoleGenModelOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCanvasConfigV3()
      .then((res) => {
        if (cancelled) return;
        setOptions(getCharacterModelOptions(res.models as CharacterModelConfig));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to load character model config'));
        setOptions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return {
    options, loading, error, reload,
  };
}
